import type { Release } from '@src/dto/Release';
import { isRight } from 'fp-ts/Either';
import { useCallback, useEffect } from 'react';
import { captureAndReportError } from '../utils/errorReporting';
import { useAsyncData } from './useAsyncData';
import { AuthStateType, useAuth } from './useAuth';
import { useTRPC } from './useTRPC';

/**
 * Releases for a dapp, shared by Overview (summary cards) and the Releases page
 * so both read the same list and stay in sync after deletes.
 */
export function useDappReleases(dappId: string | null | undefined) {
  const trpc = useTRPC();
  const [authState] = useAuth();
  const {
    data: releases,
    setData: setReleases,
    isLoading,
    setIsLoading,
    error,
    setError,
    run,
  } = useAsyncData<Release[]>({
    initialData: [],
    initialLoading: true,
  });

  const refetch = useCallback(() => {
    if (!dappId) {
      return Promise.resolve();
    }

    return run(() => trpc.release.listReleases.query({ dappId }), {
      showLoadingState: false,
      clearError: false,
      onSuccess: (result, helpers) => {
        if (isRight(result)) {
          helpers.setData(result.right);
          helpers.setError(null);
        } else {
          helpers.setError(result.left.message || 'Failed to load releases');
        }
        helpers.setIsLoading(false);
      },
      onError: (loadError, helpers) => {
        captureAndReportError('Failed to fetch releases:', loadError);
        helpers.setError('Failed to load releases');
        helpers.setIsLoading(false);
      },
    }).then(() => undefined);
  }, [dappId, run, trpc]);

  useEffect(() => {
    if (!dappId) {
      setIsLoading(false);
      return;
    }

    if (authState.type === AuthStateType.Authenticated) {
      setIsLoading(true);
      const timer = setTimeout(() => {
        void refetch();
      }, 100);
      return () => clearTimeout(timer);
    }

    if (authState.type === AuthStateType.NoToken) {
      setError('Authentication required');
      setIsLoading(false);
    }
  }, [authState.type, dappId, refetch, setError, setIsLoading]);

  return { releases, setReleases, isLoading, error, refetch };
}
